import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { clearCsrfToken, ensureCsrfToken } from "./Csrf";
import { globalEventEmitter, GlobalEvents } from "./GlobalEventEmitter";

const MUTATING_METHODS = ["post", "put", "patch", "delete"];

type CsrfRetriableConfig = InternalAxiosRequestConfig & { csrfRetried?: boolean };

// Rails rejects a missing or stale CSRF token with this exact body. A bare 403 is an
// authorisation failure (CanCan) and must not be mistaken for one.
const isCsrfRejection = (error: AxiosError<{ error?: string }>) =>
  error.response?.status === 403 && error.response.data?.error === "invalid_csrf_token";

/**
 * Auth is carried by the Rails session cookie, which the browser attaches automatically
 * as long as `withCredentials` is set. Nothing here reads or writes the credential --
 * the cookie is HttpOnly.
 *
 * This requires the API to send an explicit `Access-Control-Allow-Origin` for this origin
 * plus `Access-Control-Allow-Credentials: true`; a wildcard origin will not work.
 */
const createAxiosInstance = () => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_BASE_API_URL,
    withCredentials: true,
    headers: {
      Accept: "application/json",
      // The backend picks the educator vs admin ability set off this header.
      "X-Client-App": "educator-webapp",
    },
  });

  // Every mutating request carries a token, signed out ones included: /api/v2/csrf mints
  // a session for an anonymous caller, so login and the password-reset routes can present
  // one just like the authenticated routes do.
  instance.interceptors.request.use(async (config) => {
    const isMutating = MUTATING_METHODS.includes((config.method ?? "get").toLowerCase());

    if (isMutating) {
      const token = await ensureCsrfToken();

      if (token) {
        config.headers.set("X-CSRF-Token", token);
      }
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<{ error?: string }>) => {
      const status = error.response?.status;

      if (status === 401) {
        globalEventEmitter.emit(GlobalEvents.API_REQUEST_UNAUTHORIZED);
      }

      // 401 means the session has gone.
      if (status === 401 || isCsrfRejection(error)) {
        clearCsrfToken();
      }

      // The cached token goes stale whenever the session rotates underneath us -- a login
      // elsewhere, a logout in another tab. Refetching and replaying once makes that
      // invisible instead of surfacing an error the user could only clear by reloading.
      const config = error.config as CsrfRetriableConfig | undefined;

      if (isCsrfRejection(error) && config && !config.csrfRetried) {
        config.csrfRetried = true;

        // The request interceptor attaches the freshly fetched token on the way back out.
        if (await ensureCsrfToken()) {
          return instance.request(config);
        }
      }

      throw error;
    },
  );

  return instance;
};

const instance = createAxiosInstance();

// The public/secured split no longer means anything -- the cookie is sent either way --
// but the shape is kept so the ~15 service modules calling these don't all need editing.
export const axiosInstances = Object.freeze({
  public: () => instance,
  secured: () => instance,
});
